<script lang="ts">
  import Select from "svelte-select"
  import { fetchCustomers } from "../../../../../../../../../api/kehrblatt/customer-registry/customers"

  type CustomerSelectItem = {
    value: string
    label: string
    customer: Customer
  }

  type Props = {
    organizationId: string
    seedCustomers?: Customer[]
    disabled?: boolean
    brightBackground?: boolean
    id?: string
    placeholder?: string
    excludeCustomerIds?: string[]
    onChange?: (customers: Customer[]) => void
  }

  type PublicProps = Props & {
    value?: string[]
  }

  const minimumSearchLength = 2
  const searchLimit = 25

  let {
    organizationId,
    seedCustomers = [],
    disabled = false,
    brightBackground = true,
    id = "customer-async-multiselect",
    placeholder = "Ansprechpartner auswählen",
    excludeCustomerIds = [],
    onChange = undefined,
    value = $bindable<string[]>([]),
  }: PublicProps = $props()

  let items = $state<CustomerSelectItem[]>([])
  let selectedItems = $state<CustomerSelectItem[]>([])
  let filterText = $state("")
  let loading = $state(false)
  let syncingSelectedItems = false
  let lastAppliedValueKey = $state<string | null>(null)
  let lastAppliedSelectionKey = $state<string | null>(null)
  let loadedCustomers = $state<Customer[]>([])

  const normalizeId = (input: string | null | undefined) => `${input ?? ""}`.trim()

  const pendingCustomerIds = new Set<string>()

  const mapCustomerToItem = (customer: Customer): CustomerSelectItem => ({
    value: normalizeId(customer.id),
    label: customer.display_name || customer.id,
    customer,
  })

  const mergeCachedCustomers = (current: Customer[], incoming: Customer[]) => {
    if (!incoming.length) return current

    const byId = new Map(current.map((entry) => [normalizeId(entry.id), entry]))
    let changed = false
    for (const customer of incoming) {
      const customerId = normalizeId(customer.id)

      if (!customerId) continue

      if (byId.get(customerId) !== customer) {
        byId.set(customerId, customer)
        changed = true
      }
    }

    return changed ? Array.from(byId.values()) : current
  }

  const cachedCustomers = $derived.by(() =>
    mergeCachedCustomers(mergeCachedCustomers([], seedCustomers), loadedCustomers),
  )
  const currentCustomerIds = $derived(
    Array.from(new Set((value ?? []).map((entry) => normalizeId(entry)).filter(Boolean))),
  )
  const excludedCustomerIdSet = $derived(
    new Set(
      (excludeCustomerIds ?? [])
        .map((entry) => normalizeId(entry))
        .filter((entry) => Boolean(entry) && !currentCustomerIds.includes(entry)),
    ),
  )
  const currentCustomerIdsKey = $derived(currentCustomerIds.join("|"))
  const currentCustomers = $derived(
    currentCustomerIds
      .map(
        (customerId) =>
          cachedCustomers.find((entry) => normalizeId(entry.id) === customerId) ?? null,
      )
      .filter((entry): entry is Customer => Boolean(entry)),
  )

  const emptyMessage = $derived.by(() => {
    if (loading) return "Suche läuft…"

    if (filterText.trim().length < minimumSearchLength) return "Mindestens 2 Zeichen für Ergebnisse"

    return "Keine Ergebnisse"
  })

  const ensureCustomersLoaded = async (customerIds: string[]) => {
    const missingIds = customerIds.filter((customerId) => {
      if (!customerId) return false

      if (pendingCustomerIds.has(customerId)) return false

      return !cachedCustomers.some((entry) => normalizeId(entry.id) === customerId)
    })

    if (!organizationId || !missingIds.length) return

    missingIds.forEach((customerId) => pendingCustomerIds.add(customerId))
    try {
      const loaded = await fetchCustomers({
        organizationId,
        customerIds: missingIds,
        limit: missingIds.length,
      })
      loadedCustomers = mergeCachedCustomers(loadedCustomers, loaded?.items ?? [])
    } catch (err) {
      console.error("customer_async_multiselect_load_failed", err)
    } finally {
      missingIds.forEach((customerId) => pendingCustomerIds.delete(customerId))
    }
  }

  const loadOptions = async (input: string) => {
    const trimmed = input.trim()

    if (trimmed.length < minimumSearchLength) {
      items = []

      return []
    }

    loading = true
    try {
      const response = await fetchCustomers({
        organizationId,
        query: trimmed,
        limit: searchLimit,
      })
      const nextItems = (response?.items ?? [])
        .filter((customer) => !excludedCustomerIdSet.has(normalizeId(customer.id)))
        .map(mapCustomerToItem)
      loadedCustomers = mergeCachedCustomers(loadedCustomers, response?.items ?? [])
      items = nextItems

      return nextItems
    } catch (err) {
      console.error("customer_async_multiselect_search_failed", err)
      items = []

      return []
    } finally {
      loading = false
    }
  }

  $effect(() => {
    if (currentCustomerIdsKey === lastAppliedValueKey) return

    if (!currentCustomerIds?.length) {
      if (selectedItems.length) {
        syncingSelectedItems = true
        selectedItems = []
        lastAppliedSelectionKey = ""
        queueMicrotask(() => {
          syncingSelectedItems = false
        })
      }

      lastAppliedValueKey = currentCustomerIdsKey
      lastAppliedSelectionKey = ""

      return
    }

    if (!currentCustomers) {
      void ensureCustomersLoaded(currentCustomerIds)

      return
    }

    if (currentCustomers.length === currentCustomerIds.length) {
      const nextItems = currentCustomers.map(mapCustomerToItem)
      const currentKey = selectedItems.map((item) => item.value).join("|")
      const nextKey = nextItems.map((item) => item.value).join("|")

      if (currentKey !== nextKey) {
        syncingSelectedItems = true
        selectedItems = nextItems
        lastAppliedSelectionKey = nextKey
        queueMicrotask(() => {
          syncingSelectedItems = false
        })
      }

      lastAppliedValueKey = currentCustomerIdsKey
      lastAppliedSelectionKey = nextKey

      return
    }

    void ensureCustomersLoaded(currentCustomerIds)
  })

  const applySelection = (nextItems: CustomerSelectItem[]) => {
    if (syncingSelectedItems) return

    const nextIds = Array.from(
      new Set(
        nextItems.map((item) => normalizeId(item.customer?.id ?? item.value)).filter(Boolean),
      ),
    )
    const nextKey = nextIds.join("|")

    if (nextKey === lastAppliedSelectionKey) return

    value = nextIds
    lastAppliedValueKey = nextKey
    lastAppliedSelectionKey = nextKey
    onChange?.(nextItems.map((item) => item.customer).filter(Boolean))
  }

  const handleSelectChange = (
    event: CustomEvent<CustomerSelectItem[] | CustomerSelectItem | null>,
  ) => {
    filterText = ""
    const detail = event.detail
    applySelection(Array.isArray(detail) ? detail : detail ? [detail] : selectedItems)
  }

  const handleSelectClear = () => {
    filterText = ""
    applySelection([])
  }
</script>

<label class="fieldShell" data-input-id={id} class:disabled>
  <span class="labelspan hasValue">{placeholder}</span>
  <div class="select-input-shell">
    <Select
      bind:value={selectedItems}
      bind:filterText
      multiple={true}
      {disabled}
      {loading}
      {loadOptions}
      {items}
      {id}
      placeholder=" "
      clearable={true}
      debounceWait={300}
      class={brightBackground ? "svelte-select--bright" : "svelte-select--dark"}
      itemId="value"
      label="label"
      showChevron={true}
      closeListOnChange={false}
      inputAttributes={{ autocomplete: "off" }}
      on:change={handleSelectChange}
      on:clear={handleSelectClear}
    >
      <svelte:fragment slot="selection" let:selection>
        <div class="select-selection">
          <span class="select-selection__label"
            >{selection?.label ?? selection?.name ?? selection?.value ?? ""}</span
          >
        </div>
      </svelte:fragment>

      <svelte:fragment slot="item" let:item>
        <div class="select-item">
          <span class="select-item__label">{item.label ?? item.name ?? item.value}</span>
        </div>
      </svelte:fragment>

      <svelte:fragment slot="empty">
        <div class="emptyState">{emptyMessage}</div>
      </svelte:fragment>
    </Select>
  </div>
</label>

<style lang="less">
  .fieldShell {
    width: 100%;
    max-width: 100%;
  }

  :global(label.fieldShell.error .svelte-select) {
    border-color: var(--warning-100);
  }

  .fieldShell :global(.svelte-select),
  .fieldShell :global(.select-input-shell) {
    width: 100%;
  }

  .emptyState {
    padding: 0.6rem 0.85rem;
    color: color-mix(in srgb, var(--text-100) 72%, transparent);
    font-size: 0.92rem;
  }
</style>
